import type { ExcludedProduct } from '@/api/types/products';

/** One table row: a Shopify product with its exclusion record (if any). */
export type ExclusionRow = {
  /** Numeric Shopify product ID (GIDs are normalized). */
  productId: string;
  title: string | null;
  /** URL slug, only present in catalog mode (exclusion records store no handle). */
  handle?: string | null;
  imageUrl?: string | null;
  exclusion: ExcludedProduct | null;
};

export type ExclusionStatus = 'active' | 'disabled';
export type ShowFilter = 'all' | 'excluded';

export type ExclusionFilterValues = {
  search: string;
  collectionId: string | null;
  /** Free text: shops can carry thousands of tags, so this is not a picker. */
  tag: string;
  methodKey: string | null;
  country: string | null;
  status: ExclusionStatus | null;
  show: ShowFilter;
};

export const EMPTY_EXCLUSION_FILTERS: ExclusionFilterValues = {
  search: '',
  collectionId: null,
  tag: '',
  methodKey: null,
  country: null,
  status: null,
  show: 'all',
};

/**
 * Whether these filter values put the list on the DB-backed exclusion source
 * rather than the Shopify catalog.
 */
export function isExclusionMode(values: ExclusionFilterValues): boolean {
  return (
    values.methodKey !== null ||
    values.country !== null ||
    values.status !== null ||
    values.show === 'excluded'
  );
}

export type MethodOption = {
  methodKey: string;
  label: string;
};

export type CountryOption = {
  code: string;
  name: string;
};

/** Design checkbox: 16px, 4px radius, neutral border on white. */
export const checkboxClass =
  'size-4 rounded-[4px] border-input bg-white shadow-sm data-[state=checked]:border-primary';

export type ScopeItem<T extends string> = { id: T; label: string };

/**
 * Turns a stored scope array into the picker's selection.
 *
 * An empty stored array is the "all of them" sentinel, so it seeds as every
 * option ticked - a blanket exclusion must not open looking like it excluded
 * nothing. A missing array (no exclusion record yet) seeds the same way, since
 * the default action is a blanket exclusion.
 */
export function seedScopeSelection<T extends string>(
  stored: readonly T[] | undefined | null,
  everything: readonly T[],
): Set<T> {
  return new Set(
    stored === undefined || stored === null || stored.length === 0 ? everything : stored,
  );
}

export function toggledSet<T>(previous: ReadonlySet<T>, id: T, checked: boolean): Set<T> {
  const next = new Set(previous);
  if (checked) next.add(id);
  else next.delete(id);
  return next;
}

export function methodItemsFrom(options: MethodOption[]): ScopeItem<string>[] {
  return options.map((option) => ({ id: option.methodKey, label: option.label }));
}

export function countryItemsFrom(options: CountryOption[]): ScopeItem<string>[] {
  return options.map((option) => ({ id: option.code, label: option.name }));
}

const PRODUCT_GID = /^gid:\/\/shopify\/Product\/(\d+)$/;

/** The products proxy returns GIDs; exclusions and the carrier callback use numeric IDs. */
export function numericProductId(id: string): string {
  const match = PRODUCT_GID.exec(id);
  return match ? match[1] : id;
}

export function countryName(code: string, locale: string): string {
  try {
    return new Intl.DisplayNames([locale], { type: 'region' }).of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

/**
 * Individual countries selectable for exclusion - the full supported set from
 * the delivery catalog (not just countries currently configured in markets),
 * sorted by localized name.
 */
export function countryOptionsFromCodes(codes: string[], locale: string): CountryOption[] {
  return [...new Set(codes.map((code) => code.toUpperCase()))]
    .map((code) => ({ code, name: countryName(code, locale) }))
    .sort((a, b) => a.name.localeCompare(b.name, locale));
}
